import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useOrganization } from "../context/OrganizationContext";

function OrgIcon({ active = false }) {
  return (
    <div
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-sm ${
        active ? "bg-primary/20 text-primary" : "bg-white/[0.06] text-white/70"
      }`}
      aria-hidden
    >
      <svg
        className="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M4 7h16M6 7v12m12-12v12M9 7v12m6-12v12M4 19h16"
        />
      </svg>
    </div>
  );
}

export default function OrganizationSelector() {
  const {
    organizations,
    currentOrganization,
    switchOrganization,
    createOrganization,
    updateOrganization,
  } = useOrganization();
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newOrgData, setNewOrgData] = useState({ name: "", description: "" });
  const [editOrgData, setEditOrgData] = useState({ name: "", description: "" });
  const [editingOrg, setEditingOrg] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onPointerDown = (e) => {
      if (!rootRef.current?.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", onPointerDown);
      document.addEventListener("touchstart", onPointerDown, { passive: true });
    }

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [isOpen]);

  const startEdit = (org) => {
    setEditingOrg(org);
    setEditOrgData({
      name: org?.name || "",
      description: org?.description || "",
    });
    setShowEditModal(true);
    setIsOpen(false);
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      await createOrganization(newOrgData);
      setShowCreateModal(false);
      setNewOrgData({ name: "", description: "" });
    } catch {
      // Error handled by context
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditOrganization = async (e) => {
    e.preventDefault();
    if (!editingOrg?.id) return;
    setIsSaving(true);
    try {
      await updateOrganization(editingOrg.id, {
        name: editOrgData.name,
        description: editOrgData.description,
      });
      setShowEditModal(false);
      setEditingOrg(null);
    } catch {
      // Error handled by context
    } finally {
      setIsSaving(false);
    }
  };

  if (!currentOrganization) {
    return (
      <motion.button
        onClick={() => setShowCreateModal(true)}
        className="inline-flex items-center gap-2 rounded-sm border border-primary bg-primary/10 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/20"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Create organization
      </motion.button>
    );
  }

  return (
    <>
      <div ref={rootRef} className="relative">
        <motion.button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          className="flex w-full items-center gap-3 rounded-sm border border-white/10 bg-[#1f1f1f] px-3 py-2.5 text-left text-white shadow-[0_1px_0_rgba(255,255,255,0.04)_inset] transition-colors hover:border-white/15 hover:bg-[#242424]"
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          <OrgIcon active />
          <div className="min-w-0 flex-1">
            <p className="font-secondary truncate text-sm font-medium text-white">
              {currentOrganization.name}
            </p>
            <p className="font-secondary truncate text-xs text-white/45">
              {currentOrganization.slug}
            </p>
          </div>
          <svg
            className={`h-5 w-5 shrink-0 text-white/45 transition-transform ${isOpen ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </motion.button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18 }}
              className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-sm border border-white/10 bg-[#131313] shadow-[0_18px_40px_rgba(0,0,0,0.45)]"
            >
              <div className="border-b border-white/10 px-3 py-2">
                <p className="font-secondary text-xs uppercase tracking-[0.16em] text-white/45">
                  Organizations
                </p>
              </div>
              <div className="max-h-72 overflow-y-auto p-2">
                {organizations.map((org) => {
                  const active = String(org.id) === String(currentOrganization.id);
                  return (
                    <div
                      key={org.id}
                      className={`flex items-center gap-2 rounded-sm px-2 py-2 transition-colors ${
                        active ? "bg-primary/10" : "hover:bg-white/[0.06]"
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          switchOrganization(org.id);
                          setIsOpen(false);
                        }}
                        className="flex min-w-0 flex-1 items-center gap-3 text-left"
                      >
                        <OrgIcon active={active} />
                        <div className="min-w-0 flex-1">
                          <p className="font-secondary truncate text-sm font-medium text-white">
                            {org.name}
                          </p>
                          <p className="font-secondary truncate text-xs text-white/45">
                            {org.slug}
                          </p>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(org);
                        }}
                        className="rounded-sm p-1.5 text-white/45 transition-colors hover:bg-white/[0.06] hover:text-primary"
                        aria-label={`Edit ${org.name}`}
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-2.586-9.414a2 2 0 112.828 2.828L11 16H8v-3l8.414-8.414z"
                          />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
              {organizations.length === 0 && (
                <div className="border-t border-white/10 p-2">
                  <motion.button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(true);
                      setIsOpen(false);
                    }}
                    className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left font-secondary text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
                    whileHover={{ x: 2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    New organization
                  </motion.button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !isCreating && setShowCreateModal(false)}
          >
            <motion.div
              className="w-full max-w-md rounded-sm border border-white/10 bg-landing-section shadow-2xl"
              initial={{ scale: 0.96, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 12 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-white/10 p-6">
                <h3 className="font-title text-xl font-bold text-white">
                  Create your organization
                </h3>
                <p className="font-secondary mt-2 text-sm text-white/60">
                  Set up your workspace to manage projects and API keys. Each account is limited to one organization.
                </p>
              </div>
              <form onSubmit={handleCreateOrganization} className="space-y-4 p-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/70">
                    Organization name
                  </label>
                  <input
                    type="text"
                    required
                    value={newOrgData.name}
                    onChange={(e) =>
                      setNewOrgData({ ...newOrgData, name: e.target.value })
                    }
                    placeholder="My organization"
                    className="font-secondary w-full rounded-sm border border-white/15 bg-[#0c0c0c] px-4 py-3 text-white placeholder:text-white/35 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/70">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    value={newOrgData.description}
                    onChange={(e) =>
                      setNewOrgData({
                        ...newOrgData,
                        description: e.target.value,
                      })
                    }
                    placeholder="Optional description"
                    className="font-secondary w-full resize-none rounded-sm border border-white/15 bg-[#0c0c0c] px-4 py-3 text-white placeholder:text-white/35 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="font-secondary rounded-sm border border-white/15 px-4 py-2.5 text-sm font-semibold text-white/70 hover:bg-white/[0.06]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isCreating}
                    className="font-secondary rounded-sm border border-primary bg-primary px-4 py-2.5 text-sm font-bold text-[#0a0a0a] hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isCreating ? "Creating..." : "Create"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEditModal && editingOrg && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !isSaving && setShowEditModal(false)}
          >
            <motion.div
              className="w-full max-w-md rounded-sm border border-white/10 bg-landing-section shadow-2xl"
              initial={{ scale: 0.96, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 12 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-white/10 p-6">
                <h3 className="font-title text-xl font-bold text-white">
                  Edit organization
                </h3>
                <p className="font-secondary mt-2 text-sm text-white/60">
                  Rename the workspace and update its description.
                </p>
              </div>
              <form onSubmit={handleEditOrganization} className="space-y-4 p-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/70">
                    Organization name
                  </label>
                  <input
                    type="text"
                    required
                    value={editOrgData.name}
                    onChange={(e) =>
                      setEditOrgData({ ...editOrgData, name: e.target.value })
                    }
                    className="font-secondary w-full rounded-sm border border-white/15 bg-[#0c0c0c] px-4 py-3 text-white placeholder:text-white/35 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/70">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    value={editOrgData.description}
                    onChange={(e) =>
                      setEditOrgData({
                        ...editOrgData,
                        description: e.target.value,
                      })
                    }
                    className="font-secondary w-full resize-none rounded-sm border border-white/15 bg-[#0c0c0c] px-4 py-3 text-white placeholder:text-white/35 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="font-secondary rounded-sm border border-white/15 px-4 py-2.5 text-sm font-semibold text-white/70 hover:bg-white/[0.06]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="font-secondary rounded-sm border border-primary bg-primary px-4 py-2.5 text-sm font-bold text-[#0a0a0a] hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSaving ? "Saving..." : "Save changes"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
